import { type NewsProviderId, NEWS_PROVIDERS } from '../shared/newsProviders';

export const INVALID_NEWSLETTER_PROVIDER_ERROR =
  'Select only supported newsletter sources.';
export const NEWSLETTER_PROVIDER_REQUIRED_ERROR =
  'Select at least one news source to receive the newsletter.';
export const NEWSLETTER_EMAIL_VERIFICATION_REQUIRED_ERROR =
  'Verify your email before subscribing to the newsletter.';

const VALID_NEWS_PROVIDER_IDS = new Set<NewsProviderId>(
  NEWS_PROVIDERS.map((provider) => provider.id),
);

export type NewsletterPreferenceState = {
  sendNewsletter: boolean;
  newsletterProviders: string[];
};

export type NewsletterPreferenceUserLike = {
  email?: string | null;
  sendNewsletter?: boolean | null;
  newsletterProviders?: readonly string[] | null;
  identities?: Record<
    string,
    {
      id?: string | null;
      isEmailVerified?: boolean | null;
    } | null
  > | null;
};

type NormalizeNewsletterPreferenceUpdateArgs = {
  currentUser?: NewsletterPreferenceUserLike | null;
  sendNewsletter: boolean;
  newsletterProviders: readonly string[];
};

function collectNewsletterProviders(
  providerIds: readonly string[] | null | undefined,
  { throwOnUnknown }: { throwOnUnknown: boolean },
): string[] {
  if (!providerIds) {
    return [];
  }

  const normalizedProviders: string[] = [];

  for (const providerId of providerIds) {
    if (!VALID_NEWS_PROVIDER_IDS.has(providerId as NewsProviderId)) {
      if (throwOnUnknown) {
        throw new Error(INVALID_NEWSLETTER_PROVIDER_ERROR);
      }

      console.warn('[newsletter] ignored stale provider id', {
        providerId,
      });
      continue;
    }

    if (!normalizedProviders.includes(providerId as NewsProviderId)) {
      normalizedProviders.push(providerId as NewsProviderId);
    }
  }

  return normalizedProviders;
}

export function normalizeNewsletterProviders(
  providerIds?: readonly string[] | null,
): string[] {
  return collectNewsletterProviders(providerIds, { throwOnUnknown: true });
}

export function filterValidNewsletterProviders(
  providerIds?: readonly string[] | null,
): string[] {
  return collectNewsletterProviders(providerIds, { throwOnUnknown: false });
}

export function getNewsletterPreferenceState(
  user?: NewsletterPreferenceUserLike | null,
): NewsletterPreferenceState {
  const newsletterProviders = filterValidNewsletterProviders(
    user?.newsletterProviders,
  );

  return {
    sendNewsletter:
      Boolean(user?.sendNewsletter) && newsletterProviders.length > 0,
    newsletterProviders,
  };
}

export function isNewsletterEligibleForSubscription(
  user?: NewsletterPreferenceUserLike | null,
): boolean {
  if (user?.identities?.email?.isEmailVerified === true) {
    return true;
  }

  if (!user?.email) {
    return false;
  }

  return Object.entries(user.identities ?? {}).some(
    ([providerName, identity]) => providerName !== 'email' && identity !== null,
  );
}

export function toggleNewsletterProviderSelection(
  state: NewsletterPreferenceState,
  providerId: NewsProviderId,
): NewsletterPreferenceState {
  if (!VALID_NEWS_PROVIDER_IDS.has(providerId)) {
    throw new Error(INVALID_NEWSLETTER_PROVIDER_ERROR);
  }

  const newsletterProviders = state.newsletterProviders.includes(providerId)
    ? state.newsletterProviders.filter(
        (selectedProviderId) => selectedProviderId !== providerId,
      )
    : [...state.newsletterProviders, providerId];

  return {
    sendNewsletter:
      newsletterProviders.length === 0 ? false : state.sendNewsletter,
    newsletterProviders,
  };
}

export function setNewsletterConsent(
  state: NewsletterPreferenceState,
  sendNewsletter: boolean,
): NewsletterPreferenceState {
  if (!sendNewsletter) {
    return {
      sendNewsletter: false,
      newsletterProviders: state.newsletterProviders,
    };
  }

  if (state.newsletterProviders.length === 0) {
    throw new Error(NEWSLETTER_PROVIDER_REQUIRED_ERROR);
  }

  return {
    sendNewsletter: true,
    newsletterProviders: state.newsletterProviders,
  };
}

export function normalizeNewsletterPreferenceUpdate({
  currentUser,
  sendNewsletter,
  newsletterProviders,
}: NormalizeNewsletterPreferenceUpdateArgs): NewsletterPreferenceState {
  const requestedProviders = normalizeNewsletterProviders(newsletterProviders);
  const resolvedProviders = requestedProviders;

  const currentlySubscribed = Boolean(currentUser?.sendNewsletter);
  const requestingSubscribe = sendNewsletter && !currentlySubscribed;

  const resolvedSendNewsletter = requestingSubscribe ? false : sendNewsletter;

  if (resolvedSendNewsletter && resolvedProviders.length === 0) {
    throw new Error(NEWSLETTER_PROVIDER_REQUIRED_ERROR);
  }

  if (
    resolvedSendNewsletter &&
    !isNewsletterEligibleForSubscription(currentUser)
  ) {
    throw new Error(NEWSLETTER_EMAIL_VERIFICATION_REQUIRED_ERROR);
  }

  return {
    sendNewsletter: resolvedSendNewsletter,
    newsletterProviders: resolvedProviders,
  };
}

export function getNewsletterErrorMessage(
  error: unknown,
  fallbackMessage: string,
): string {
  if (error instanceof Error && error.message.trim().length > 0) {
    return error.message;
  }

  return fallbackMessage;
}
